import React, { useState, useEffect } from 'react';
import { Row, Col, Button, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { InputNumber } from 'primereact';
import { formatToDecimals } from '@lib/helpers/formatting';
import { OETrans } from 'components/translation/OETrans';
import { getAllProjectNoticeExpenseItems } from '@lib/api';

const EconomicFrameworkStream = ({
	projectId,
	isOe,
	isExternalAdmin,
	applicationEconomicFrameworkValues,
	totalAmounts,
	totalControls,
	onChange,
	saveEconomicsFramework,
	localLang,
}) => {
	const [projectNoticeExpenseItems, setProjectNoticeExpenseItems] = useState([]);
	//console.log('projectNoticeExpenseItems', projectNoticeExpenseItems);
	const [isModified, setIsModified] = useState(false);
	const handleChange = (e, id, name) => {
		onChange(e, id, name);
		setIsModified(true);
	};

	useEffect(() => {
		if (projectId) {
			getAllProjectNoticeExpenseItems(projectId).then((noticeExpenseItems) => {
				setProjectNoticeExpenseItems(
					noticeExpenseItems.data.map((noticeExpenseItem) => ({
						id: noticeExpenseItem.id,
						name: noticeExpenseItem.name,
						projectId: noticeExpenseItem.projectId,
						noticeExpenseItemId: noticeExpenseItem.noticeExpenseItemId,
						position: noticeExpenseItem.position,
					})),
				);
			});
		}
	}, [projectId]);

	const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

	const economicsTab = () => {
		const colHeader = [<th key="th-empty"></th>];

		let colHeaderX = [
			'Costo progetto (€)',
			'Costo ammissibile (€)',
			'Costo approvato (€)',
			"Intensità d'aiuto (%)",
			'Contributo richiesto (€)',
		];

		colHeader.push(
			colHeaderX.map((colH) => (
				<th key={colH} className={`${!isExternalAdmin ? 'text-end' : ''} no-to-head cell-width-economic-data f-600 text-end`}>
					{colH}
				</th>
			)),
		);

		let rowHeaderX = projectNoticeExpenseItems.sort((a, b) => {
			return a.position - b.position;
		});

		rowHeaderX = rowHeaderX.filter((item, index) => rowHeaderX.indexOf(item) === index);

		return (
			<div>
				<table className="show-economics-tab">
					<thead>
						<tr>{colHeader}</tr>
					</thead>
					<tbody className="text-end">
						{rowHeaderX.map((rowH, index) => {
							return (
								<tr key={`${rowH.id}-${rowH.name}`}>
									<td className="pl-2 f-600 py-3">{rowH.name}</td>
									{applicationEconomicFrameworkValues
										.filter((el) => rowH.id === Number(el.projectNoticeExpenseItemId))
										.map((el) => {
											return (
												<React.Fragment key={`el-${el.id}`}>
													{isOe || isExternalAdmin ? (
														<>
															<td>
																<InputNumber
																	id={el.id}
																	key={el.id}
																	name="amount"
																	min={0}
																	step={1}
																	minFractionDigits={2}
																	maxFractionDigits={2}
																	onChange={(e) => {
																		handleChange(e, el.id, 'amount');
																	}}
																	mode="decimal"
																	locale={localLang}
																	className="ml input-candidacy justify-content-end"
																	value={el.amount ?? 0}
																/>
															</td>
															<td>
																<InputNumber
																	key={el.id}
																	name="admissibleAmount"
																	min={0}
																	step={1}
																	minFractionDigits={2}
																	maxFractionDigits={2}
																	//disabled
																	//readOnly
																	onChange={(e) => {
																		handleChange(e, el.id, 'admissibleAmount');
																	}}
																	mode="decimal"
																	locale={localLang}
																	className="md input-candidacy justify-content-end"
																	value={el.admissibleAmount ?? 0}
																/>
															</td>
															<td>
																<InputNumber
																	key={el.id}
																	name="approvedAmount"
																	min={0}
																	step={1}
																	minFractionDigits={2}
																	maxFractionDigits={2}
																	onChange={(e) => {
																		handleChange(e, el.id, 'approvedAmount');
																	}}
																	mode="decimal"
																	locale={localLang}
																	className="md input-candidacy justify-content-end"
																	value={el.approvedAmount ?? 0}
																/>
															</td>
															<td>
																<InputNumber
																	name="intensity"
																	step={1}
																	minFractionDigits={2}
																	maxFractionDigits={10}
																	onChange={(e) => {
																		handleChange(e, el.id, 'intensity');
																	}}
																	mode="decimal"
																	locale={localLang}
																	className="ml input-candidacy justify-content-end"
																	value={el.intensity ?? 0}
																	min={0}
																	max={100}
																/>
															</td>
														</>
													) : (
														<>
															<td className="py-3">
																<span className="text-end">{formatToDecimals(el.amount)}</span>
															</td>
															<td className="py-3">
																<span className="text-end">{formatToDecimals(el.admissibleAmount)}</span>
															</td>
															<td className="py-3">
																<span className="text-end">{formatToDecimals(el.approvedAmount)}</span>
															</td>
															<td className="py-3">
																<span className="text-end">{formatToDecimals(el.intensity)}</span>
															</td>
														</>
													)}
													<td className="py-3">
														<span className="text-end">{formatToDecimals(el.requiredAmount ?? 0)}</span>
													</td>
												</React.Fragment>
											);
										})}
								</tr>
							);
						})}

						<tr className="totals-row" style={{ borderTop: '1px solid var(--np-accent-color)' }}>
							<td className="text-left py-3">
								<span className="mb-0 f-600" style={{ textTransform: 'uppercase' }}>
									Totale
								</span>
							</td>
							<td className="text-end">
								<div className={`${!isExternalAdmin ? 'me-0' : ''}`}>
									{Object.keys(totalAmounts).length > 0 && Object.keys(totalControls).length > 0 ? (
										<ConditionalWrapper
											condition={false} //totalAmounts.projectAmount !== totalControls?.totalAmount (da concordare)
											wrapper={(children) => (
												<OverlayTrigger
													placement="bottom"
													overlay={
														<Tooltip>
															Il valore non corrisponde a quanto presente in Dashboard:
															<br />
															{formatToDecimals(totalControls?.totalAmount)} €
														</Tooltip>
													}
												>
													<span className="text-danger">{children}</span>
												</OverlayTrigger>
											)}
										>
											<span className="f-600" style={{ paddingRight: !isExternalAdmin ? '0' : '13px' }}>
												{formatToDecimals(totalAmounts.projectAmount)}
											</span>
										</ConditionalWrapper>
									) : (
										<Spinner
											animation="border"
											role="status"
											size="sm"
											style={{ marginTop: '0.5rem', marginRight: !isExternalAdmin ? '0' : '10px' }}
										>
											<span style={{ visibility: 'hidden' }}>
												<OETrans code="common.loading" fallback={'Caricamento'} />
												...
											</span>
										</Spinner>
									)}
								</div>
							</td>
							<td className="text-end py-4">
								<div className={`f-600 ${!isExternalAdmin ? 'me-0' : ''}`}>
									<span style={{ paddingRight: !isExternalAdmin ? '0' : '13px' }}>{formatToDecimals(totalAmounts.admissibleAmount)}</span>
								</div>
							</td>
							<td className="text-end py-4">
								<div className={`f-600 ${!isExternalAdmin ? 'me-0' : ''}`}>
									<span>{formatToDecimals(totalAmounts.approvedAmount)}</span>
								</div>
							</td>
							<td className="text-end py-3"></td>
							<td className="text-end py-3"></td>
						</tr>

						<tr className="totals-row">
							<td className="text-left py-3">
								<span className="mb-0 f-600 no-to-head" style={{ textTransform: 'uppercase' }}>
									Contributo concesso
								</span>
							</td>
							<td className="text-end py-3"></td>
							<td className="text-end py-3"></td>
							<td className="text-end py-3"></td>
							<td className="text-end py-3"></td>
							<td className="text-end py-3 f-600">
								<span>{formatToDecimals(totalAmounts.requiredAmount ? totalAmounts.requiredAmount : 0)}</span>
							</td>
						</tr>
					</tbody>
				</table>
				{(isOe || isExternalAdmin) && (
					<Row className="mt-5 mb-2 text-center">
						<Col>
							<Button onClick={saveEconomicsFramework} disabled={!isModified}>
								<OETrans code="common.save" fallback={'Salva'} />
							</Button>
						</Col>
					</Row>
				)}
			</div>
		);
	};

	return <>{totalAmounts && totalControls ? economicsTab() : null}</>;
};
export default EconomicFrameworkStream;
